import {
  createContext,
  useContext,
  useEffect,
  useId,
  useRef,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Button, type IconName } from './primitives';
import { useBelow } from '../lib/useMediaQuery';

// Structure: the eyebrow label, the card, the two small controls that sit in a
// card's header row (a segmented filter and a period stepper), and the add form
// a card's header button discloses.

// ── Eyebrow ───────────────────────────────────────────────────────────────────
// The small uppercase label. It was re-implemented by SectionHeader, PCard,
// KpiTile, OrgSettings labelCSS and PeopleHR fieldLabelStyle — 72 recipes in 6
// sizes (8.5–11px) and 8 trackings (DS-12). One size, one tracking, one colour.
type EyebrowTag = 'span' | 'div' | 'p' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
export const Eyebrow = ({
  as: Tag = 'span',
  id,
  children,
  style,
}: {
  /** A heading tag when the label names a section (Card uses h2). */
  as?: EyebrowTag;
  id?: string;
  children?: ReactNode;
  style?: CSSProperties;
}) => (
  <Tag
    id={id}
    style={{
      margin: 0,
      fontSize: 'var(--hf-text-2xs)',
      fontWeight: 'var(--hf-weight-medium)',
      letterSpacing: 'var(--hf-track-eyebrow)',
      textTransform: 'uppercase',
      color: 'var(--hf-ink-3)',
      ...style,
    }}
  >
    {children}
  </Tag>
);

// ── Surface ───────────────────────────────────────────────────────────────────
// Elevation (Design Spec §4.4, UI-CONVENTIONS §8): one glass layer per stack.
// A Card on the page is glass; inside a Card or a Dialog it is a flat
// .hf-panel, and a KpiTile there is a bare figure. Card and Dialog mark what
// they hold, so a nested one picks its surface from where it is rendered — no
// prop to remember. A hand-built surface (MeScreen's identity header) wraps
// its contents in <Surface> to say the same.
const InSurface = createContext(false);
export const Surface = ({ children }: { children?: ReactNode }) => (
  <InSurface.Provider value>{children}</InSurface.Provider>
);
// A hook beside its provider is the pattern (as useToast); nothing here holds
// state for Fast Refresh to lose.
// eslint-disable-next-line react-refresh/only-export-components
export const useInSurface = () => useContext(InSurface);

// ── Card ──────────────────────────────────────────────────────────────────────
// PCard, moved out of people.tsx (DS-18) — 74 call sites, so its look is kept.
// Its label was a styled <span>: /me showed seven visually titled cards and no
// headings at all, and /timesheets went h1 → h3 (ME-V2, XA-13, TS-20). The
// label is now an h2 that names the section, so every card is a landmark a
// screen reader can list and jump to. Nested in another surface it is a
// panel, and its label an h3 unless `level` says otherwise.
export const Card = ({
  label,
  level,
  badge,
  right,
  children,
  style = {},
}: {
  label?: string;
  level?: 2 | 3 | 4;
  /** Beside the label, before the gap: the Claude Design's section tag ("calculated"). */
  badge?: ReactNode;
  right?: ReactNode;
  children?: ReactNode;
  style?: CSSProperties;
}) => {
  const id = useId();
  const nested = useInSurface();
  return (
    <section
      className={nested ? 'hf-panel' : 'hf-glass'}
      aria-labelledby={label ? id : undefined}
      // 18px and 14px are PCard's off-grid values, kept so 74 cards don't shift;
      // they move to the nearest step when their screens are swept. A panel's
      // radius steps down so its corners sit concentric inside the card's.
      style={{
        borderRadius: nested ? 'var(--hf-radius-md)' : 'var(--hf-radius-card)',
        padding: 'var(--hf-space-4) 18px',
        minWidth: 0,
        ...style,
      }}
    >
      {label && (
        <div
          style={{
            display: 'flex',
            // At 320px a wide `right` goes under the label instead of past
            // the card's edge.
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--hf-space-2)',
            marginBottom: 14,
          }}
        >
          <Eyebrow as={`h${level ?? (nested ? 3 : 2)}`} id={id}>
            {label}
          </Eyebrow>
          {badge}
          <div style={{ flex: 1 }} />
          {right}
        </div>
      )}
      <Surface>{children}</Surface>
    </section>
  );
};

// ── SegmentedControl ──────────────────────────────────────────────────────────
// One choice out of a few (Pending / Approved / All). The HR filter chips showed
// their state by background colour alone, with no role or state (HRA-19), and
// OrgSeg, OrgAccessSeg and the holiday year picker are three more copies. A
// radiogroup with the Tabs keyboard contract: one Tab stop, arrows and
// Home/End move and select. `count` is a dimmed facet of the list on screen —
// allowed by UI-CONVENTIONS §1, and not an attention count.
// It reads the .hf-screen-bar / .hf-screen-chip classes the prototype left in
// tokens.css with no reader, so the selected paint lives in a class.
export const SegmentedControl = <T extends string>({
  label,
  options,
  value,
  onChange,
  full,
}: {
  /** What is being chosen, for screen readers ("Request status"). */
  label: string;
  options: readonly { value: T; label: string; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  /** Span the row, the chips sharing it: a phone's one-control-per-row forms. */
  full?: boolean;
}) => {
  const current = Math.max(
    0,
    options.findIndex((o) => o.value === value),
  );
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[e.key];
    const n = options.length;
    const next =
      step != null ? (current + step + n) % n : e.key === 'Home' ? 0 : e.key === 'End' ? n - 1 : -1;
    if (next < 0) return;
    e.preventDefault();
    onChange(options[next].value);
    e.currentTarget.querySelectorAll<HTMLButtonElement>('[role="radio"]')[next]?.focus();
  };
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="hf-screen-bar"
      style={full ? { display: 'flex', width: '100%' } : undefined}
      onKeyDown={onKeyDown}
    >
      {options.map((o, i) => {
        const on = i === current && o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={on}
            tabIndex={i === current ? 0 : -1}
            // hf-hit: 44px tall under a coarse pointer (tokens.css).
            className={on ? 'hf-screen-chip hf-hit active' : 'hf-screen-chip hf-hit'}
            // .active is a surface-3 tint, ~1.2:1 on the bar: state by a faint
            // colour change alone, HRA-19 itself (WCAG 1.4.11). The control
            // edge gives the chosen chip a shape. Inline because that rule sits
            // in the prototype block of tokens.css.
            style={{
              ...(on && { boxShadow: 'inset 0 0 0 1px var(--hf-rule-control)' }),
              // Grown to fill, so the chips' own padding can be small: four
              // holiday types then fit a 320px phone instead of scrolling.
              ...(full && { flex: '1 0 auto', paddingInline: 'var(--hf-space-1)' }),
            }}
            onClick={() => onChange(o.value)}
          >
            {o.label}
            {/* A real space, so the radio is named "Pending 4", not "Pending4". */}
            {o.count != null && (
              <>
                {' '}
                <span style={{ color: 'var(--hf-ink-3)', fontFamily: 'var(--hf-font-mono)' }}>
                  {o.count}
                </span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
};

// ── PeriodStepper ─────────────────────────────────────────────────────────────
// ‹ September 2026 › — the three hand-rolled navBtn copies (28/26/24px) were
// icon-only with just a `title`, "disabled" by opacity, and the "This week"
// reset appeared BETWEEN the arrows, shifting the next button under the pointer
// (TS-11, HRA-12). Named buttons, real `disabled`, a label slot that doesn't
// resize with the text, and the reset after the arrows in a slot that is
// reserved whenever `resetLabel` is given, so nothing moves when it appears.
export const PeriodStepper = ({
  label,
  unit,
  onPrev,
  onNext,
  prevDisabled,
  nextDisabled,
  onReset,
  resetLabel,
  labelWidth = '9.5rem',
}: {
  /** The period on screen, e.g. "September 2026". Announced when it changes. */
  label: string;
  /** Names the buttons: "Previous month", "Next month". */
  unit: 'day' | 'week' | 'month' | 'year';
  onPrev: () => void;
  onNext: () => void;
  prevDisabled?: boolean;
  nextDisabled?: boolean;
  /** Pass only while away from the current period; the slot stays reserved. */
  onReset?: () => void;
  resetLabel?: string;
  /** Fixed, so Next never moves: size it for the longest label, e.g. a week
   *  range across two years. Longer text is cut with an ellipsis on screen and
   *  still announced in full. */
  labelWidth?: string;
}) => {
  // Below md the row wraps rather than run past a 320px card, and the reset is
  // what goes to the second line, after Next, so the arrows still never move.
  // A reserved but empty slot there was a blank line under the stepper, so on
  // a phone it is drawn only while it has something to say.
  const phone = useBelow('md');
  return (
    <div
      style={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 'var(--hf-space-2)',
      }}
    >
      <StepButton icon="chevronLeft" onClick={onPrev} disabled={prevDisabled}>
        Previous {unit}
      </StepButton>
      <span
        aria-live="polite"
        style={{
          width: labelWidth,
          flexShrink: 0,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          textAlign: 'center',
          fontSize: 'var(--hf-text-sm)',
          color: 'var(--hf-ink)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {label}
      </span>
      <StepButton icon="chevronRight" onClick={onNext} disabled={nextDisabled}>
        Next {unit}
      </StepButton>
      {resetLabel && (onReset || !phone) && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onReset}
          // Hidden, not removed: the arrows keep their place. A hidden button is
          // neither focusable nor announced.
          style={{ visibility: onReset ? 'visible' : 'hidden' }}
        >
          {resetLabel}
        </Button>
      )}
    </div>
  );
};

// Icon-only, so the name is visually hidden text rather than a `title`. The
// most-tapped small control on a phone screen: .hf-hit gives the 32px box a
// 44px area under a finger (tokens.css).
const StepButton = ({
  icon,
  onClick,
  disabled,
  children,
}: {
  icon: IconName;
  onClick: () => void;
  disabled?: boolean;
  children: ReactNode;
}) => (
  <Button
    size="sm"
    icon={icon}
    onClick={onClick}
    disabled={disabled}
    className="hf-hit"
    style={{ padding: 'var(--hf-space-2)' }}
  >
    <span className="hf-sr-only">{children}</span>
  </Button>
);

// ── DisclosedForm ─────────────────────────────────────────────────────────────
/**
 * A form disclosed in place (HRP-18): the card's job is its list or its text,
 * and the form to add or edit opens from a button in the card's header. That
 * button is gone while the form is open, so the form takes focus onto its first
 * control when it opens, and hands it back to the button (`opener`, the id of a
 * span round it) when it closes, by Cancel or a save. Both used to drop it on
 * <body>, and the keyboard started again from the top of the page. Used by
 * keyDates.tsx, documents.tsx and the People record's job description; it
 * moved here from keyDates.tsx so Org's add forms can take it too (critique
 * 2026-10-05 #4, UI-CONVENTIONS §6).
 */
export function DisclosedForm({
  opener,
  first = 'input, select, textarea, button',
  style,
  children,
}: {
  opener: string;
  /** What takes focus on open: the first control, unless the form's point is
   *  a field further in (the job description's text, past its Write/Preview). */
  first?: string;
  style?: CSSProperties;
  children: ReactNode;
}) {
  const self = useRef<HTMLDivElement>(null);
  useEffect(() => {
    self.current?.querySelector<HTMLElement>(first)?.focus();
    // Runs after the commit that removed the form, so the button is back in the
    // header. Only when focus went with the form: never take it from elsewhere.
    return () => {
      if (document.activeElement === document.body)
        document.getElementById(opener)?.querySelector('button')?.focus();
    };
  }, [opener, first]);
  return (
    <div ref={self} style={style}>
      {children}
    </div>
  );
}
